import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

export const postPositionTracker = createAsyncThunk(
  'positionTracker/post',
  async ({ selectedBrands, selectedAffiliate, brandName, country, countryCode }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker/addPosition`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedBrands, selectedAffiliate, brandName, country, countryCode }),
      });
      const data = await response.json();
      if (!response.ok) {
        const message = data?.message || 'Failed To Insert Position Tracker';
        throw new Error(message);
      }
      // JUST RETURN THE DATA - Remove the response.status() code
      return data;  // This is what .unwrap() will receive
    } catch (err) {
      return rejectWithValue(err?.message || 'Unknown Error');
    }
  }
);

export const fetchPositionTrackerList = createAsyncThunk(
  'positionTracker/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker/list`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        const message = data?.message || 'Failed To Fetch Position Tracker List';
        throw new Error(message);
      }
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Unknown Error');
    }
  }
);

// Start crawl - Sends data to external service
export const startCrawl = createAsyncThunk(
  'positionTracker/startCrawl',
  async ({ G_Brand_Name, G_Brand_Url, G_Affiliate_Url,G_User_Id,G_Brande_Id,trackingId, country, countryCode }, { rejectWithValue }) => {
    try {

      // This will be sent to external service (you'll provide the endpoint)
      const response = await fetch(`${basePath}/api/position-tracker/crawl`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          G_Brand_Name,
          G_Brand_Url,
          G_Affiliate_Url,
          G_User_Id,
          G_Brande_Id,
          trackingId,
          country,
          countryCode
        }),
      });

      const data = await response.json();

      // Handle brand not found case (success: false but status 200)
      if (data.success === false && data.message) {
        // Return the full error data including suggestions
        return rejectWithValue({
          message: data.message,
          suggestions: data.suggestions || [],
          data: data.data
        });
      }

      if (!response.ok) {
        const message = data?.message || 'Failed to start crawl';
        throw new Error(message);
      }

      return { ...data, trackingId };
    } catch (err) {
      return rejectWithValue(err?.message || 'Unknown Error');
    }
  }
);

// Get position score results
export const getPositionScore = createAsyncThunk(
  'positionTracker/getScore',
  async ({ trackingId }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker/score/${trackingId}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (!response.ok) {
        const message = data?.message || 'Failed to get position score';
        throw new Error(message);
      }

      return data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Unknown Error');
    }
  }
);

const initialState = {
  data: null,
  postLoading: false,
  fetchLoading: false,
  trackingList: [],
  error: null,
  crawlLoading: false,
  crawlError: null,
  crawlResult: null,
  currentTrackingId: null,
  scoreLoading: false,
  scoreError: null,
  scoreResult: null,
};

const positionTrackerSlice = createSlice({
  name: 'positiontracker',
  initialState,
  reducers: {
    resetTracker: (state) => {
      state.data = null;
      state.error = null;
      state.postLoading = false;
      state.fetchLoading = false;
      state.crawlLoading = false;
      state.crawlError = null;
      state.crawlResult = null;
      state.currentTrackingId = null;
    },
    setTrackerData(state, action) {
      state.data = action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postPositionTracker.pending, (state) => {
        state.postLoading = true;
        state.error = null;
      })
      .addCase(postPositionTracker.fulfilled, (state, action) => {
        state.postLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(postPositionTracker.rejected, (state, action) => {
        state.postLoading = false;
        state.error = action.payload || action.error?.message || 'Something Went Wrong';
      })
      .addCase(fetchPositionTrackerList.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(fetchPositionTrackerList.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.trackingList = action.payload;
        state.error = null;
      })
      .addCase(fetchPositionTrackerList.rejected, (state, action) => {
        state.fetchLoading = false;
        state.error = action.payload || action.error?.message || 'Failed To Fetch List';
      })
      .addCase(startCrawl.pending, (state) => {
        state.crawlLoading = true;
        state.crawlError = null;
      })
      .addCase(startCrawl.fulfilled, (state, action) => {
        state.crawlLoading = false;
        state.crawlResult = action.payload;
        state.currentTrackingId = action.payload.trackingId;
        state.crawlError = null;
      })
      .addCase(startCrawl.rejected, (state, action) => {
        state.crawlLoading = false;
        // If payload is an object with suggestions, store it as-is
        // Otherwise, convert to string
        if (typeof action.payload === 'object' && action.payload !== null) {
          state.crawlError = action.payload;
        } else {
          state.crawlError = {
            message: action.payload || action.error?.message || 'Failed to start crawl',
            suggestions: []
          };
        }
      })
      .addCase(getPositionScore.pending, (state) => {
        state.scoreLoading = true;
        state.scoreError = null;
      })
      .addCase(getPositionScore.fulfilled, (state, action) => {
        state.scoreLoading = false;
        state.scoreResult = action.payload;
        state.scoreError = null;
      })
      .addCase(getPositionScore.rejected, (state, action) => {
        state.scoreLoading = false;
        state.scoreError = action.payload || action.error?.message || 'Failed to get score';
      });
  },
});

export const { resetTracker, setTrackerData } = positionTrackerSlice.actions;
export default positionTrackerSlice.reducer;